import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BookOpenCheck, BrainCircuit, Building2, CheckCircle2, Eye, FileText, Inbox, ListTodo, Loader2,
  MailCheck, Newspaper, Pause, PenLine, Play, Radar, Scale, Sparkles, Tags,
  Clock, CornerUpLeft, Rows3, Zap,
} from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import { useReveal } from "@/hooks/useReveal";
import { cn } from "@/lib/utils";
import { isNative, nativeSignIn } from "@/lib/native";
import { AppleSignInButton } from "@/components/common/AppleSignInButton";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleSignIn = () => {
  // Native (iOS/Android): open sign-in in the system browser and return via the discern:// scheme.
  if (isNative()) {
    nativeSignIn();
    return;
  }
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ------------------------------------------------ primitives */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={cn(
        "transition-[opacity,transform] duration-700 ease-out",
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
        className
      )}
    >
      {children}
    </div>
  );
};

const Kicker = ({ children }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <span className="h-px w-8 bg-[hsl(var(--editorial-red))]" />
    <span className="text-xs uppercase tracking-[0.18em] font-medium text-[hsl(var(--editorial-red))]">{children}</span>
  </div>
);

const Wordmark = () => (
  <span className="flex items-center gap-2">
    <span className="w-2 h-2 bg-[hsl(var(--editorial-red))]" aria-hidden="true" />
    <span className="font-heading font-semibold tracking-tight text-lg">Discern</span>
  </span>
);

const Typewriter = ({ text, active, speed = 12 }) => {
  const [len, setLen] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (reducedMotion()) {
      setLen(text.length);
      return undefined;
    }
    setLen(0);
    const id = setInterval(() => {
      setLen((l) => {
        if (l >= text.length) {
          clearInterval(id);
          return l;
        }
        return l + 2;
      });
    }, speed);
    return () => clearInterval(id);
  }, [active, text, speed]);
  return (
    <span>
      {text.slice(0, len)}
      {len < text.length && <span className="caret-blink text-[hsl(var(--editorial-red))]">▌</span>}
    </span>
  );
};

/** Browser-style frame around a real product screenshot. */
const ScreenFrame = ({ src, alt, url = "app.discern", className = "", imgClassName = "", eager = false, testId }) => (
  <div
    className={cn("rounded-lg border border-[hsl(var(--hairline))] bg-card card-shadow overflow-hidden", className)}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 px-3 h-8 border-b border-[hsl(var(--hairline))] bg-muted/60">
      <span className="flex gap-1.5" aria-hidden="true">
        <span className="w-2 h-2 rounded-full bg-foreground/15" />
        <span className="w-2 h-2 rounded-full bg-foreground/15" />
        <span className="w-2 h-2 rounded-full bg-foreground/15" />
      </span>
      <span className="mx-auto text-[10px] font-mono text-muted-foreground truncate">{url}</span>
    </div>
    <img
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      className={cn("block w-full h-auto", imgClassName)}
    />
  </div>
);

/* ------------------------------------------------ signal ticker */
const TICKER_ITEMS = [
  "BIGCORP RAISES $40M SERIES B · RENEWAL LEVERAGE DETECTED",
  "YOUR TERM SHEET OPENED 3× · SAN FRANCISCO, 8:42 AM",
  "@NORTHLIGHTVC PUBLISHED A NEW FUND THESIS",
  "MERIDIAN LEGAL WENT QUIET: 12 DAYS MID-CONTRACT",
  "PRESS SIGNAL: TECHDAILY WRITING ON AI PRODUCTIVITY",
  "YOUR POST IS TAKING OFF: 240 LIKES IN 3 HOURS",
  "COMMITMENT DUE: “DECK BY FRIDAY”, PROMISED TUESDAY",
];

const SignalTicker = () => (
  <div className="bg-primary text-primary-foreground overflow-hidden select-none" aria-hidden="true" data-testid="signal-ticker">
    <div className="ticker-track flex w-max items-center py-1.5">
      {[0, 1].map((dup) => (
        <div key={dup} className="flex items-center shrink-0">
          {TICKER_ITEMS.map((t) => (
            <span key={`${dup}-${t}`} className="flex items-center text-[10px] font-mono tracking-[0.12em] whitespace-nowrap">
              <span className="px-5">{t}</span>
              <span className="text-[hsl(var(--editorial-red))] text-[8px]">◆</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------ masthead */
const NAV = [["How it works", "how"], ["Product", "product"], ["A morning", "morning"], ["Features", "features"], ["FAQ", "faq"], ["Access", "access"]];

const Masthead = ({ user }) => (
  <header className="sticky top-0 z-40 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-[hsl(var(--hairline))]">
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 h-16 flex items-center justify-between">
      <Wordmark />
      <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
        {NAV.map(([label, id]) => (
          <button key={id} onClick={() => scrollTo(id)} className="hover:text-foreground transition-colors" data-testid={`nav-${id}-link`}>
            {label}
          </button>
        ))}
      </nav>
      <div className="flex items-center gap-2">
        {user ? (
          <Button asChild size="sm" data-testid="open-app-button">
            <Link to={user.access === "pending" ? "/invite" : "/dashboard"}>
              Open Discern <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Link>
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={handleSignIn} data-testid="masthead-signin-button">
              Have an invite? Sign in
            </Button>
            <AppleSignInButton size="sm" />
            <Button size="sm" onClick={() => scrollTo("access")} data-testid="masthead-request-access-button">
              Request access
            </Button>
          </>
        )}
      </div>
    </div>
  </header>
);

/* ------------------------------------------------ hero + live card stack */
const HERO_CARDS = [
  {
    id: "brief",
    body: (
      <>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">This morning</div>
        <p className="font-heading text-[15px] font-semibold leading-snug">3 threads need answers. BigCorp renewal at risk of drifting. Last reply 6 days ago.</p>
        <div className="flex gap-1.5 mt-3">
          <Badge variant="secondary" className="text-[10px]"><Tags className="w-3 h-3 mr-1" /> client · high</Badge>
          <Badge variant="secondary" className="text-[10px]"><MailCheck className="w-3 h-3 mr-1" /> opened 2×</Badge>
        </div>
      </>
    ),
  },
  {
    id: "signal",
    body: (
      <>
        <div className="flex items-center gap-2 mb-2">
          <Radar className="w-3.5 h-3.5 text-[hsl(var(--editorial-red))]" />
          <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Signal → Action</span>
        </div>
        <p className="text-sm leading-snug">Northlight VC just published its new fund thesis. <span className="font-semibold">Draft a note to Marcus</span> while it&rsquo;s fresh.</p>
        <div className="flex gap-2 mt-3">
          <span className="inline-flex items-center rounded-md bg-primary text-primary-foreground text-[11px] font-medium px-2.5 py-1"><PenLine className="w-3 h-3 mr-1" /> Draft email</span>
          <span className="inline-flex items-center rounded-md border border-border text-[11px] font-medium px-2.5 py-1"><ListTodo className="w-3 h-3 mr-1" /> Create task</span>
        </div>
      </>
    ),
  },
  {
    id: "receipt",
    body: (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="w-3.5 h-3.5 text-accent shrink-0" />
          <span className="text-sm truncate">Term sheet opened in <span className="font-semibold">San Francisco</span>, 8:42 AM</span>
        </div>
        <Badge className="bg-amber-600 text-white text-[10px] shrink-0">3 opens</Badge>
      </div>
    ),
  },
  {
    id: "silence",
    body: (
      <>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Silence signal</div>
        <p className="text-sm leading-snug"><span className="font-semibold">Meridian Legal went quiet</span>, 12 days mid-contract. A gentle nudge is drafted and waiting.</p>
      </>
    ),
  },
  {
    id: "social",
    body: (
      <>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Your post is taking off</div>
        <p className="text-sm leading-snug">240 likes in 3 hours, 4 investors in the replies. <span className="font-semibold">Two reply drafts ready.</span></p>
      </>
    ),
  },
];

const HeroCardStack = () => {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    if (reducedMotion()) return undefined;
    const id = setInterval(() => setOffset((o) => (o + 1) % HERO_CARDS.length), 4500);
    return () => clearInterval(id);
  }, []);
  const indents = ["", "lg:ml-8", "lg:ml-3"];
  return (
    <div className="space-y-3" aria-hidden="true" data-testid="hero-card-stack">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--editorial-red))] live-dot" />
        Signals, as they land
      </div>
      {[0, 1, 2].map((slot) => {
        const card = HERO_CARDS[(offset + slot) % HERO_CARDS.length];
        return (
          <div
            key={`${offset}-${card.id}`}
            style={{ animationDelay: `${slot * 110}ms` }}
            className={cn("bg-card rounded-md border border-[hsl(var(--hairline))] p-4 card-shadow animate-card-in", indents[slot])}
          >
            {card.body}
          </div>
        );
      })}
    </div>
  );
};

const Hero = () => (
  <section className="mx-auto w-full max-w-6xl px-4 sm:px-6 pt-14 sm:pt-20 lg:pt-24 pb-14">
    <div className="lg:grid lg:grid-cols-12 lg:gap-10 items-center">
      <div className="lg:col-span-7">
        <Kicker>The smartest email client for founders</Kicker>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">
          Know what matters.<br />Act before it&rsquo;s urgent.
        </h1>
        <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-[58ch]">
          Discern is the smartest email client for founders. A keyboard-fast inbox that reads and ranks
          your mail, splits it the way you think, chases the replies you&rsquo;re owed, and turns news and
          social signals into actions you can fire in one click.
        </p>
        <div className="mt-8 flex items-center gap-3 flex-wrap">
          <Button size="lg" className="h-11 px-6" onClick={() => scrollTo("access")} data-testid="hero-request-access-button">
            Request access
          </Button>
          <Button size="lg" variant="outline" className="h-11 px-6" onClick={() => scrollTo("product")} data-testid="hero-how-button">
            See the product
          </Button>
        </div>
        <p className="mt-4 text-xs text-muted-foreground" data-testid="hero-proof-line">
          Private rollout in small cohorts · requests answered within a day or two.
        </p>
      </div>
      <div className="lg:col-span-5 mt-12 lg:mt-0">
        <HeroCardStack />
      </div>
    </div>
  </section>
);

/* ------------------------------------------------ how it works */
const STEPS = [
  { n: "01", title: "Connect Gmail", desc: "Your inbox syncs in minutes. Every send carries a read receipt." },
  { n: "02", title: "Discern organizes", desc: "AI tags, filters, company profiles and relationship intelligence, built from your own history." },
  { n: "03", title: "Signals become actions", desc: "News and social activity about the people you work with arrive as suggested moves: a drafted email or a task." },
];

const HowItWorks = () => (
  <section id="how" className="border-y border-[hsl(var(--hairline))] bg-muted/40">
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-12 sm:py-14">
      <div className="grid md:grid-cols-3 gap-8 md:gap-0">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 120} className={`md:px-8 ${i > 0 ? "md:border-l md:border-[hsl(var(--hairline))]" : "md:pl-0"}`}>
            <div className="font-heading text-3xl font-semibold text-[hsl(var(--editorial-red))]">{s.n}</div>
            <h3 className="font-heading text-lg font-semibold mt-2">{s.title}</h3>
            <p className="text-sm text-muted-foreground leading-6 mt-1.5">{s.desc}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------ inside Discern: real product screens */
const SCREENS = [
  {
    id: "dashboard",
    n: "01",
    time: "08:00",
    narration: "One decision, before the inbox.",
    title: "The Lead Story",
    icon: Newspaper,
    caption: "One decision before the inbox. Discern reads overnight email, tasks, commitments and signals, then names the single highest-leverage call for today, with the first move.",
    url: "app.discern/dashboard",
    alt: "Discern dashboard showing the Lead Story card with a decision, first move and actions",
  },
  {
    id: "inbox",
    n: "02",
    time: "08:03",
    narration: "Tagged and ranked. Reply, task or snooze in one click.",
    title: "The Inbox",
    icon: Inbox,
    caption: "Already tagged, ranked, and keyboard-fast. Split your inbox into lanes, fly with J and K, and open threads with AI draft replies, tags, tasks and snooze a keystroke away. Every send carries a read receipt.",
    url: "app.discern/inbox",
    alt: "Discern inbox with a tagged thread open and AI draft reply actions",
  },
  {
    id: "signals",
    n: "03",
    time: "08:06",
    narration: "A raise, a post, a quiet thread. Each becomes an action.",
    title: "Signals",
    icon: Radar,
    caption: "News and social activity about the companies and people you work with, turned into action cards: a drafted email or a task, ready to fire.",
    url: "app.discern/signals",
    alt: "Discern Signals page with an action queue and watchlist",
  },
  {
    id: "ledger",
    n: "04",
    time: "08:08",
    narration: "Promises tracked. Yesterday's decision, logged.",
    title: "The Ledger",
    icon: Scale,
    caption: "Every promise you made in email, tracked to done. Every morning decision logged with how it went, so tomorrow's front page learns from it.",
    url: "app.discern/ledger",
    alt: "Discern Ledger showing the decision log with outcomes and streak",
  },
  {
    id: "investor",
    n: "05",
    time: "08:10",
    narration: "Written from what actually happened. Asks tracked until answered.",
    title: "The Investor Update",
    icon: FileText,
    caption: "Drafted from your real last 30 days, in your voice. Each ask in the update becomes a tracked item until someone says yes.",
    url: "app.discern/review",
    alt: "Discern investor update draft beside the Ask Tracker panel",
  },
];

const STEP_MS = 12000; // 5 screens x 12s = one morning in 60 seconds

const ProductScreens = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 within the current step
  const touched = useRef(false);
  const activeRef = useRef(0);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // Idle mode: gentle auto-advance until the visitor interacts
  useEffect(() => {
    if (reducedMotion() || paused || playing || touched.current) return undefined;
    const id = setInterval(() => setActive((a) => (a + 1) % SCREENS.length), 6500);
    return () => clearInterval(id);
  }, [paused, playing]);

  // Play mode: the walkthrough, 12s per screen with a progress bar
  useEffect(() => {
    if (!playing) return undefined;
    let start = performance.now();
    const id = setInterval(() => {
      const p = (performance.now() - start) / STEP_MS;
      if (p < 1) {
        setProgress(p);
        return;
      }
      if (activeRef.current + 1 >= SCREENS.length) {
        setProgress(1);
        setPlaying(false);
        setFinished(true);
        return;
      }
      activeRef.current += 1;
      setActive(activeRef.current);
      start = performance.now();
      setProgress(0);
    }, 100);
    return () => clearInterval(id);
  }, [playing]);

  const choose = (i) => {
    touched.current = true;
    setPlaying(false);
    setProgress(0);
    setActive(i);
  };
  const play = () => {
    touched.current = true;
    setFinished(false);
    setProgress(0);
    activeRef.current = 0;
    setActive(0);
    setPlaying(true);
  };
  const pause = () => setPlaying(false);
  const current = SCREENS[active];
  const overall = playing || finished ? (active + progress) / SCREENS.length : 0;

  return (
    <section id="product" className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-14 sm:py-16" data-testid="product-section">
      <Reveal>
        <Kicker>Inside Discern</Kicker>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">The product, not a promise.</h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-[52ch]">Real screens from a working account with demo data. Nothing mocked up.</p>
          </div>
          <Button
            variant={playing ? "outline" : "default"}
            className="h-10 px-4 shrink-0"
            onClick={playing ? pause : play}
            aria-pressed={playing}
            data-testid="product-play-button"
          >
            {playing ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {playing ? "Pause" : finished ? "Replay the morning" : "Play one morning in 60 seconds"}
          </Button>
        </div>
      </Reveal>

      <Reveal delay={120} className="mt-8">
        <div
          className="lg:grid lg:grid-cols-12 lg:gap-8 items-start"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* tab strip */}
          <div className="lg:col-span-4 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0" role="tablist" aria-label="Product screens">
            {SCREENS.map((s, i) => {
              const Icon = s.icon;
              const selected = i === active;
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => choose(i)}
                  className={cn(
                    "text-left shrink-0 w-[220px] lg:w-full rounded-md border px-4 py-3 transition-colors",
                    selected
                      ? "bg-card border-[hsl(var(--hairline))] card-shadow border-l-2 border-l-[hsl(var(--editorial-red))]"
                      : "border-transparent hover:bg-muted/60"
                  )}
                  data-testid={`product-tab-${s.id}`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className={cn("font-heading text-sm font-semibold", selected ? "text-[hsl(var(--editorial-red))]" : "text-muted-foreground")}>{s.n}</span>
                    <Icon className={cn("w-3.5 h-3.5", selected ? "text-foreground" : "text-muted-foreground")} />
                    <span className={cn("font-heading text-[15px] font-semibold", !selected && "text-foreground/80")}>{s.title}</span>
                  </div>
                  <p className={cn("hidden lg:block text-xs leading-5 mt-1.5 text-muted-foreground", !selected && "line-clamp-2")}>{s.caption}</p>
                </button>
              );
            })}
          </div>

          {/* screen */}
          <div className="lg:col-span-8 mt-4 lg:mt-0">
            <div className="relative aspect-[16/10]" data-testid="product-frame">
              {SCREENS.map((s, i) => (
                <div
                  key={s.id}
                  className={cn(
                    "absolute inset-0 transition-opacity duration-500 ease-out",
                    i === active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
                  )}
                  aria-hidden={i !== active}
                >
                  <ScreenFrame
                    src={`/screens/${s.id}.webp`}
                    alt={s.alt}
                    url={s.url}
                    eager={i === 0}
                    className="h-full"
                    imgClassName="h-[calc(100%-2rem)] object-cover object-top"
                    testId={i === active ? "product-screen-image" : undefined}
                  />
                </div>
              ))}
              {(playing || finished) && (
                <div
                  key={current.id}
                  className="absolute left-3 right-3 bottom-3 z-20 flex items-center gap-3 rounded-md bg-primary/90 text-primary-foreground px-3.5 py-2.5 backdrop-blur animate-card-in"
                  data-testid="product-step-label"
                >
                  <span className="font-mono text-[11px] tracking-[0.14em] shrink-0">{current.time}</span>
                  <span className="text-sm leading-snug">{current.narration}</span>
                  {finished && <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.14em] opacity-80 shrink-0">Eight minutes, done</span>}
                </div>
              )}
            </div>
            <p className="lg:hidden text-sm text-muted-foreground leading-6 mt-4" data-testid="product-caption">
              <span className="font-semibold text-foreground">{current.title}. </span>{current.caption}
            </p>
            {playing || finished ? (
              <div className="mt-4 h-1 w-full rounded-full bg-foreground/10 overflow-hidden" data-testid="product-progress" aria-hidden="true">
                <div
                  className="h-full bg-[hsl(var(--editorial-red))] transition-[width] duration-100 ease-linear"
                  style={{ width: `${Math.round(overall * 100)}%` }}
                />
              </div>
            ) : (
              <div className="hidden lg:flex items-center gap-1.5 mt-4" aria-hidden="true">
                {SCREENS.map((s, i) => (
                  <span key={s.id} className={cn("h-1 rounded-full transition-[width,background-color] duration-300", i === active ? "w-6 bg-[hsl(var(--editorial-red))]" : "w-2 bg-foreground/15")} />
                ))}
              </div>
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

/* ------------------------------------------------ principles + FAQ */
const PRINCIPLES = [
  { n: "I", text: "Founders don't need more information. They need the one call that matters, before the inbox makes it for them." },
  { n: "II", text: "A promise made in email is a promise. Track it to done, or drop it on purpose. Never let it drift." },
  { n: "III", text: "The feed ends where the action begins. Every signal should leave as a drafted email or a task, or not at all." },
];

const Principles = () => (
  <section className="border-y border-[hsl(var(--hairline))] bg-muted/40" data-testid="principles-section">
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-12 sm:py-14">
      <Reveal>
        <Kicker>What we believe</Kicker>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-8 md:gap-0 mt-2">
        {PRINCIPLES.map((q, i) => (
          <Reveal key={q.n} delay={i * 120} className={`md:px-8 ${i > 0 ? "md:border-l md:border-[hsl(var(--hairline))]" : "md:pl-0"}`}>
            <div className="font-heading text-2xl font-semibold text-[hsl(var(--editorial-red))]">{q.n}</div>
            <p className="font-heading italic text-lg sm:text-xl leading-snug text-foreground/85 mt-2" data-testid="principle-quote">{q.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* PLACEHOLDER SAMPLE QUOTES. Replace each entry with a real, founder-approved quote before launch.
   Attribution is role-based on purpose (no invented real names/companies) so nothing implies a
   specific person endorsed the product until you swap in approved wording. Set to [] to hide the
   section entirely. Shape: { quote, name, role }. */
const TESTIMONIALS = [
  {
    quote:
      "Discern tells me the one reply that actually moves the company, before the inbox decides for me. I clear the morning in ten minutes.",
    name: "Seed-stage SaaS founder",
    role: "Discern private cohort",
  },
  {
    quote:
      "Split inbox plus keyboard shortcuts turned email from a chore into a sprint. It is genuinely the smartest client I have used.",
    name: "Solo founder, developer tools",
    role: "Discern private cohort",
  },
  {
    quote:
      "It chases the replies I forget to. Two deals I would have let drift closed because Discern kept them in front of me.",
    name: "Co-founder, fintech",
    role: "Discern private cohort",
  },
];

const Testimonials = () => {
  if (TESTIMONIALS.length === 0) return null;
  return (
    <section id="founders" className="border-t border-[hsl(var(--hairline))]" data-testid="testimonials-section">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-14 sm:py-16">
        <Reveal>
          <Kicker>From the cohort</Kicker>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">In founders' words.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 120}>
              <figure className="h-full rounded-md border border-[hsl(var(--hairline))] bg-card p-6 card-shadow" data-testid="testimonial-card">
                <blockquote className="font-heading italic text-lg leading-snug text-foreground/90">"{t.quote}"</blockquote>
                <figcaption className="mt-4 text-sm">
                  <span className="font-semibold">{t.name}</span>
                  {t.role && <span className="text-muted-foreground">, {t.role}</span>}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQ_ITEMS = [
  {
    q: "How does Discern access my email?",
    a: "You connect Gmail with Google's own sign-in. Discern reads and sends through the official Gmail API using only the permissions shown on Google's consent screen. Every email you send goes out from your own address, and you can disconnect at any time from Settings.",
  },
  {
    q: "Is Discern a full email client?",
    a: "Yes. Once Gmail is connected, Discern is a fast, keyboard-first client: read, reply, forward, archive, snooze, search, split your inbox into sections, and run bulk actions, all without leaving the keyboard. On top of that it tags and ranks threads, tracks your sends, chases the replies you are owed, and turns signals into one-click actions.",
  },
  {
    q: "What does the AI see, and is my data used to train models?",
    a: "The AI reads the threads it needs to tag, draft and brief you, and writes back only into your own workspace. Your email is not used to train models. Shared scorecards contain counts and your own headlines, never message content.",
  },
  {
    q: "What does invite-only mean?",
    a: "We onboard founders in small cohorts so we can stay close while the product matures. Request access on this page and we will reply to every request, usually within a day or two. Invites can be revoked, and you can leave whenever you like.",
  },
  {
    q: "Which accounts can I connect?",
    a: "Gmail today, including several addresses unified in one inbox. Signals draw on public news, plus X and Instagram through their official APIs once you connect them in Settings. More providers arrive as the cohort asks for them.",
  },
  {
    q: "What does it cost?",
    a: "Pricing for the private cohort is shared with your invite. No card is required to request access, and nothing is charged until you decide to stay.",
  },
];

const FAQ = () => (
  <section id="faq" className="border-t border-[hsl(var(--hairline))]" data-testid="faq-section">
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-14 sm:py-16">
      <div className="lg:grid lg:grid-cols-12 lg:gap-12">
        <Reveal className="lg:col-span-4">
          <Kicker>Questions</Kicker>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">Straight answers.</h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-[40ch]">
            The five things founders ask before they connect an inbox. Anything else, ask when you request access.
          </p>
        </Reveal>
        <Reveal delay={120} className="lg:col-span-8 mt-6 lg:mt-0">
          <Accordion type="single" collapsible className="w-full">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`} className="border-[hsl(var(--hairline))]" data-testid="faq-item">
                <AccordionTrigger className="text-left font-heading text-base sm:text-lg font-semibold hover:no-underline py-4" data-testid={`faq-trigger-${i}`}>
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-[15px] leading-7 text-muted-foreground pb-5" data-testid={`faq-content-${i}`}>
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------ one morning with Discern */
const BRIEF_TEXT =
  "Good morning. Three things matter today: 1) BigCorp's security review is the only renewal blocker. Close it. 2) Marcus at Northlight opened your deck twice yesterday; he's warm, move now. 3) Meridian Legal has gone quiet 9 days mid-contract; a gentle nudge is drafted.";

const MockEmailRow = ({ from, subject, tags, hot }) => (
  <div className={cn("px-4 py-3 flex items-center gap-3", hot && "bg-secondary/60")}>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold truncate">{from}</div>
      <div className="text-xs text-muted-foreground truncate">{subject}</div>
    </div>
    {tags.map((t) => <Badge key={t} variant="secondary" className="text-[10px] shrink-0">{t}</Badge>)}
    {hot && <span className="text-[10px] font-medium text-[hsl(var(--editorial-red))] shrink-0">needs answer</span>}
  </div>
);

const MorningDemo = () => {
  const [step, setStep] = useState("brief");
  return (
    <section id="morning" className="border-t border-[hsl(var(--hairline))]">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-14 sm:py-16">
        <Reveal>
          <Kicker>One morning with Discern</Kicker>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">Eight minutes, start to done.</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-[60ch]">Click through a real morning. This is the actual shape of the product.</p>
        </Reveal>
        <Reveal delay={120} className="mt-6">
          <Tabs value={step} onValueChange={setStep}>
            <TabsList className="flex w-full sm:w-auto overflow-x-auto justify-start h-auto p-1">
              {[["brief", "08:00 · The brief"], ["inbox", "08:03 · Inbox"], ["signal", "08:06 · A signal lands"], ["action", "08:07 · One click"]].map(([v, label]) => (
                <TabsTrigger key={v} value={v} className="text-xs whitespace-nowrap" data-testid={`morning-tab-${v}`}>{label}</TabsTrigger>
              ))}
            </TabsList>

            <div className="mt-4 bg-card rounded-md border border-[hsl(var(--hairline))] card-shadow min-h-[210px]">
              <TabsContent value="brief" className="m-0 p-5 sm:p-6" data-testid="morning-panel-brief">
                <div className="font-heading text-[13px] font-semibold tracking-wider border-b border-border pb-1.5 mb-3">YOUR MORNING BRIEF</div>
                <p className="text-sm sm:text-[15px] leading-7 text-foreground/90 max-w-[70ch] min-h-[84px]">
                  <Typewriter text={BRIEF_TEXT} active={step === "brief"} />
                </p>
              </TabsContent>

              <TabsContent value="inbox" className="m-0" data-testid="morning-panel-inbox">
                <div className="px-5 pt-4 pb-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">Already tagged, already ranked</div>
                <Separator />
                <MockEmailRow from="Sarah Mitchell · BigCorp" subject="Re: Security review + renewal terms" tags={["client", "high"]} hot />
                <Separator />
                <MockEmailRow from="Marcus Chen · Northlight" subject="Series A follow-up materials" tags={["investor"]} />
                <Separator />
                <MockEmailRow from="Amanda Foster · TechDaily" subject="Press: AI productivity feature" tags={["press"]} />
              </TabsContent>

              <TabsContent value="signal" className="m-0 p-5 sm:p-6" data-testid="morning-panel-signal">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 text-red-700 px-2 py-0.5 text-[10px] font-medium">high</span>
                  <Badge variant="secondary" className="text-[10px]">BigCorp</Badge>
                  <span className="text-[11px] text-muted-foreground">· news, 21 min ago</span>
                </div>
                <h4 className="font-heading text-base font-semibold mt-2">BigCorp announces $40M Series B</h4>
                <p className="text-sm text-muted-foreground leading-6 mt-1 max-w-[62ch]">
                  Your renewal conversation stalled last week. Fresh capital changes the conversation: congratulate Sarah and put the expansion on the table today.
                </p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => setStep("action")} data-testid="morning-signal-draft-button">
                    <PenLine className="w-3.5 h-3.5 mr-1.5" /> Draft email
                  </Button>
                  <Button size="sm" variant="outline" data-testid="morning-signal-task-button" onClick={() => setStep("action")}>
                    <ListTodo className="w-3.5 h-3.5 mr-1.5" /> Create task
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="action" className="m-0 p-5 sm:p-6" data-testid="morning-panel-action">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Drafted from your history in 4 seconds</div>
                <div className="rounded-md border border-border bg-background p-4 max-w-xl">
                  <div className="text-xs text-muted-foreground">To: <span className="font-mono">sarah.mitchell@bigcorp.com</span></div>
                  <div className="text-sm font-semibold mt-1.5">Congrats on the Series B, and a thought on scaling the pilot</div>
                  <p className="text-sm leading-6 text-foreground/90 mt-2">
                    Sarah, huge congratulations on the raise. Given where you&rsquo;re heading, it feels like the right moment
                    to talk about expanding the pilot to the full team. I can have the security review closed out by Thursday…
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="inline-flex items-center rounded-md bg-primary text-primary-foreground text-[11px] font-medium px-2.5 py-1"><MailCheck className="w-3 h-3 mr-1" /> Send tracked email</span>
                    <span className="text-[10px] text-muted-foreground">Preview. The real one sends from your Gmail with a read receipt.</span>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </Reveal>
      </div>
    </section>
  );
};

/* ------------------------------------------------ feature previews */
const InboxPreview = () => (
  <div className="bg-card rounded-md border border-[hsl(var(--hairline))] overflow-hidden card-shadow">
    {[
      { from: "Sarah Mitchell", subject: "Renewal terms + pilot expansion", tags: ["client", "high"], open: true },
      { from: "Amanda Foster", subject: "Press: feature on AI productivity", tags: ["press"], open: false },
      { from: "Marcus Chen", subject: "Series A follow-up materials", tags: ["investor"], open: true },
    ].map((r, i) => (
      <div key={r.subject}>
        {i > 0 && <Separator />}
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{r.from}</div>
            <div className="text-xs text-muted-foreground truncate">{r.subject}</div>
          </div>
          {r.tags.map((t) => <Badge key={t} variant="secondary" className="text-[10px] shrink-0">{t}</Badge>)}
          {r.open && <Eye className="w-3.5 h-3.5 text-accent shrink-0" />}
        </div>
      </div>
    ))}
  </div>
);

const SignalPreview = () => {
  const [mode, setMode] = useState("signal");
  return (
    <div className="bg-card rounded-md border border-[hsl(var(--hairline))] p-4 card-shadow min-h-[190px]" data-testid="signal-preview-card">
      {mode === "signal" && (
        <div className="animate-card-in">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 text-red-700 px-2 py-0.5 text-[10px] font-medium">high</span>
            <Badge variant="secondary" className="text-[10px]">BigCorp</Badge>
          </div>
          <h4 className="font-heading text-sm font-semibold mt-2">Congratulate Sarah on the Series B and propose the expansion now</h4>
          <p className="text-xs text-muted-foreground leading-5 mt-1">BigCorp announced $40M this morning. Your renewal conversation stalled last week; fresh capital changes the conversation.</p>
          <div className="flex gap-2 mt-3">
            <button onClick={() => setMode("email")} className="inline-flex items-center rounded-md bg-primary text-primary-foreground text-[11px] font-medium px-2.5 py-1 hover:opacity-90 transition-opacity" data-testid="signal-preview-draft-chip">
              <PenLine className="w-3 h-3 mr-1" /> Draft email
            </button>
            <button onClick={() => setMode("task")} className="inline-flex items-center rounded-md border border-border text-[11px] font-medium px-2.5 py-1 hover:bg-muted transition-colors" data-testid="signal-preview-task-chip">
              <ListTodo className="w-3 h-3 mr-1" /> Create task
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Try it: click one.</p>
        </div>
      )}
      {mode === "email" && (
        <div className="animate-card-in" data-testid="signal-preview-email">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Drafted in 4 seconds</div>
          <div className="text-xs text-muted-foreground">To: <span className="font-mono">sarah.mitchell@bigcorp.com</span></div>
          <div className="text-sm font-semibold mt-1">Congrats on the Series B, and a thought on scaling</div>
          <p className="text-xs leading-5 text-foreground/90 mt-1.5">Sarah, huge congratulations on the raise. Feels like the right moment to talk about expanding the pilot to the full team…</p>
          <button onClick={() => setMode("signal")} className="text-[11px] underline underline-offset-2 text-muted-foreground hover:text-foreground mt-3 transition-colors" data-testid="signal-preview-back">← back to the signal</button>
        </div>
      )}
      {mode === "task" && (
        <div className="animate-card-in" data-testid="signal-preview-taskcard">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Added to your board</div>
          <div className="rounded-md border border-border bg-background p-3">
            <div className="text-sm font-semibold">Propose expansion to BigCorp post-raise</div>
            <p className="text-xs text-muted-foreground mt-1">Congratulate Sarah, close security review, table the full-team pilot.</p>
            <div className="flex gap-1.5 mt-2">
              <Badge variant="secondary" className="text-[10px]">high</Badge>
              <Badge variant="outline" className="text-[10px]">To do</Badge>
            </div>
          </div>
          <button onClick={() => setMode("signal")} className="text-[11px] underline underline-offset-2 text-muted-foreground hover:text-foreground mt-3 transition-colors" data-testid="signal-preview-back">← back to the signal</button>
        </div>
      )}
    </div>
  );
};

const CompanyPreview = () => (
  <div className="bg-card rounded-md border border-[hsl(var(--hairline))] p-4 card-shadow">
    <div className="flex items-center justify-between">
      <div>
        <div className="font-heading text-sm font-semibold">BigCorp Industries</div>
        <div className="text-[11px] font-mono text-muted-foreground">bigcorp.com</div>
      </div>
      <Badge className="bg-amber-600 text-white text-[10px]">warming</Badge>
    </div>
    <Separator className="my-3" />
    <p className="text-xs leading-5 text-muted-foreground">Active enterprise client mid-renewal. Sarah Mitchell (VP Ops) drives the deal; security review is the open blocker. Last 7 days: 4 emails in, 2 out, 100% open rate.</p>
  </div>
);

const BriefingPreview = () => (
  <div className="bg-card rounded-md border border-[hsl(var(--hairline))] p-4 card-shadow">
    <div className="font-heading text-[13px] font-semibold tracking-wider border-b border-border pb-1">THE WEEK IN REVIEW</div>
    <p className="text-xs leading-5 text-muted-foreground mt-2">Strong outbound week, replies up 30%. Two relationships need attention: Meridian Legal has gone quiet mid-contract, and the Northlight intro thread is 9 days stale. Monday&rsquo;s priority: close the BigCorp security review.</p>
  </div>
);

const FounderOSPreview = () => (
  <ScreenFrame
    src="/screens/ledger.webp"
    alt="The Founder's Ledger with the decision log, outcomes and streak"
    url="app.discern/ledger"
    imgClassName="aspect-[16/10] object-cover object-top"
    testId="founder-os-preview"
  />
);

/* small keyboard chip + row used by the speed and follow-up previews */
const Kbd = ({ children }) => (
  <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded border border-border bg-muted text-[10px] font-mono text-foreground/70">{children}</kbd>
);

const MockRow = ({ from, subject, tag, hot }) => (
  <div className={cn("px-4 py-2.5 flex items-center gap-3", hot && "bg-secondary/40")}>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold truncate">{from}</div>
      <div className="text-xs text-muted-foreground truncate">{subject}</div>
    </div>
    {tag && <Badge variant="secondary" className="text-[10px] shrink-0">{tag}</Badge>}
  </div>
);

const SectionHeader = ({ color, label, count }) => (
  <div className="px-4 py-1.5 bg-secondary/50 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1.5">
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
    {label}
    <span className="ml-auto tabular-nums">{count}</span>
  </div>
);

const SpeedInboxPreview = () => (
  <div className="bg-card rounded-md border border-[hsl(var(--hairline))] overflow-hidden card-shadow" data-testid="speed-inbox-preview">
    <div className="flex items-center justify-between px-4 py-2 border-b border-[hsl(var(--hairline))] bg-muted/40">
      <div className="flex items-center gap-2 text-xs font-medium"><Rows3 className="w-3.5 h-3.5 text-accent" /> Split Inbox</div>
      <div className="hidden sm:flex items-center gap-1 text-[10px] text-muted-foreground">
        <Kbd>J</Kbd><Kbd>K</Kbd> move · <Kbd>R</Kbd> reply · <Kbd>E</Kbd> archive
      </div>
    </div>
    <SectionHeader color="#2563eb" label="Investors" count={2} />
    <MockRow from="Marcus · Northlight" subject="Re: term sheet draft" tag="investor" hot />
    <Separator />
    <MockRow from="Ada · Vertex" subject="Excited about your raise" tag="investor" />
    <SectionHeader color="#059669" label="Customers" count={1} />
    <MockRow from="Sarah · BigCorp" subject="Renewal terms + pilot" tag="client" />
    <SectionHeader color="#64748b" label="Everything else" count={1} />
    <MockRow from="TechDaily" subject="Press: AI productivity" tag="press" />
  </div>
);

const FollowupsPreview = () => (
  <div className="bg-card rounded-md border border-[hsl(var(--hairline))] p-4 card-shadow" data-testid="followups-preview">
    <div className="flex items-center gap-2 text-xs font-medium mb-3"><Clock className="w-3.5 h-3.5 text-accent" /> Waiting on a reply</div>
    <div className="space-y-2">
      {[
        { to: "ada@vertex.vc", subj: "Re: term sheet", d: "18d" },
        { to: "lee@acme.com", subj: "Can you confirm the terms?", d: "12d" },
      ].map((r) => (
        <div key={r.to} className="rounded-md border border-border bg-background p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[11px] text-muted-foreground truncate">To {r.to}</div>
              <div className="text-sm font-medium truncate">{r.subj}</div>
            </div>
            <Badge variant="secondary" className="text-[10px] tabular-nums shrink-0">{r.d}</Badge>
          </div>
          <div className="flex gap-1.5 mt-2">
            <span className="inline-flex items-center rounded-md bg-primary text-primary-foreground text-[10px] font-medium px-2 py-0.5"><Sparkles className="w-3 h-3 mr-1" /> AI draft</span>
            <span className="inline-flex items-center rounded-md border border-border text-[10px] font-medium px-2 py-0.5"><CornerUpLeft className="w-3 h-3 mr-1" /> Follow up</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const FEATURES = [
  {
    id: "features",
    kicker: "The Founder OS",
    title: "Decide the day before the inbox does.",
    bullets: [
      "The Lead Story: one decision each morning, chosen from everything that landed overnight",
      "The Ledger: every promise you made in email, tracked to done, with one-click chasers",
      "The Decision Log: outcomes recorded, streaks kept, and tomorrow's brief learns from it",
    ],
    quote: "Founders don't need more information. They need the one call that matters.",
    preview: <FounderOSPreview />,
    icon: BookOpenCheck,
  },
  {
    id: "inbox",
    kicker: "The inbox",
    title: "An inbox that reads itself first.",
    bullets: [
      "AI tags every thread (client, investor, legal, press) with domain rules you control",
      "Filters and priorities turn volume into a work queue",
      "Every send is tracked: opens, counts, and where they happened",
    ],
    quote: "You stop scanning. You start deciding.",
    preview: <InboxPreview />,
    icon: Inbox,
    flip: true,
  },
  {
    id: "speed",
    kicker: "Command speed",
    title: "The fastest inbox you have ever used.",
    bullets: [
      "Superhuman-style shortcuts: fly with J and K, then reply, archive, snooze or compose without touching the mouse",
      "Split Inbox stacks your mail into the lanes you work in: investors, customers, everything else",
      "Bulk label or move a whole selection in one move, then turn it into a rule so it routes itself next time",
      "Custom inboxes with precise sender, keyword and mute rules, and a live preview of exactly what they catch",
    ],
    quote: "Triage at the speed of thought.",
    preview: <SpeedInboxPreview />,
    icon: Zap,
  },
  {
    id: "followups",
    kicker: "Follow-ups",
    title: "Never let a reply drift.",
    bullets: [
      "Waiting on a reply surfaces the mail you sent that went quiet, and only when a reply was actually expected",
      "One click drafts the nudge in your voice, ready to send from your own address",
      "Set the cadence per person, or mute the ones who never write back",
    ],
    quote: "A promise made in email is a promise. Track it to done.",
    preview: <FollowupsPreview />,
    icon: Clock,
    flip: true,
  },
  {
    id: "signals",
    kicker: "Signals",
    title: "News and posts, turned into next moves.",
    bullets: [
      "A watchlist built automatically from your inbox and pipeline",
      "News, X and Instagram signals about the companies and people you work with",
      "AI action cards: every signal becomes a drafted email or a task, in one click",
    ],
    quote: "The feed ends where the action begins.",
    preview: <SignalPreview />,
    icon: Radar,
  },
  {
    id: "companies",
    kicker: "Relationships",
    title: "Every company, profiled from your own history.",
    bullets: [
      "Relationship summaries, key people and talking points, extracted from real correspondence",
      "Momentum signals: who's warming, who's cooling, who went quiet",
      "Company news in context, next action always suggested",
    ],
    quote: "Walk into every conversation already briefed.",
    preview: <CompanyPreview />,
    icon: Building2,
    flip: true,
  },
  {
    id: "briefing",
    kicker: "The briefing",
    title: "Your week, written up like a memo.",
    bullets: [
      "A morning digest that leads with today's one decision, delivered to your inbox",
      "Reply 'done' or 'kept 2' to the digest and your Ledger updates itself",
      "A weekly review and an investor update, drafted from what actually happened",
    ],
    quote: "Judgment, on a schedule.",
    preview: <BriefingPreview />,
    icon: BrainCircuit,
  },
];

const FeatureSpread = ({ f }) => (
  <div id={f.id} className="py-14 sm:py-16 border-t border-[hsl(var(--hairline))] first:border-t-0">
    <div className="lg:grid lg:grid-cols-12 lg:gap-12 items-center">
      <Reveal className={`lg:col-span-6 ${f.flip ? "lg:order-2" : ""}`}>
        <Kicker>{f.kicker}</Kicker>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">{f.title}</h2>
        <ul className="mt-5 space-y-2.5">
          {f.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-sm sm:text-[15px] leading-6 text-[hsl(var(--ink-soft,222_30%_18%))]">
              <CheckCircle2 className="w-4 h-4 text-accent mt-1 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <blockquote className="mt-6 border-l-2 border-[hsl(var(--editorial-red))] pl-4 font-heading italic text-lg text-foreground/80">
          {f.quote}
        </blockquote>
      </Reveal>
      <Reveal delay={140} className={`lg:col-span-6 mt-8 lg:mt-0 ${f.flip ? "lg:order-1" : ""}`}>
        {f.preview}
      </Reveal>
    </div>
  </div>
);

/* ------------------------------------------------ request access */
const RequestAccessSection = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", reason: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSending(true);
    try {
      await api.post("/access/request", form);
      setSent(true);
      toast.success("Request received. We'll reply soon.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="access" className="border-t border-[hsl(var(--hairline))] bg-muted/40">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-14 sm:py-20">
        <div className="lg:grid lg:grid-cols-12 lg:gap-12">
          <Reveal className="lg:col-span-5">
            <Kicker>Access</Kicker>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">Invite-only, on purpose.</h2>
            <p className="mt-4 text-sm sm:text-[15px] leading-7 text-muted-foreground max-w-[52ch]">
              Discern reads your real correspondence, so we onboard founders in small cohorts and
              stay close while we do. Tell us a little about yourself and we&rsquo;ll send an invite
              when a seat opens.
            </p>
            <p className="mt-4 text-xs text-muted-foreground">
              Already invited?{" "}
              <button onClick={handleSignIn} className="underline underline-offset-2 hover:text-foreground transition-colors" data-testid="access-signin-link">
                Sign in with Google
              </button>
            </p>
          </Reveal>
          <Reveal delay={140} className="lg:col-span-7 mt-8 lg:mt-0">
            <div className="bg-card rounded-md border border-[hsl(var(--hairline))] card-shadow p-6">
              {sent ? (
                <div className="py-8 text-center" data-testid="request-access-success">
                  <CheckCircle2 className="w-8 h-8 text-accent mx-auto mb-3" />
                  <h3 className="font-heading text-lg font-semibold">Request received</h3>
                  <p className="text-sm text-muted-foreground mt-1">We reply to every request, usually within a day or two.</p>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs mb-1.5 block">Name *</Label>
                      <Input value={form.name} onChange={set("name")} placeholder="Jane Founder" data-testid="request-access-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block">Work email *</Label>
                      <Input type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" data-testid="request-access-email-input" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label className="text-xs mb-1.5 block">Company</Label>
                    <Input value={form.company} onChange={set("company")} placeholder="Company / what you're building" data-testid="request-access-company-input" />
                  </div>
                  <div className="mt-4">
                    <Label className="text-xs mb-1.5 block">What would you use Discern for?</Label>
                    <Textarea value={form.reason} onChange={set("reason")} placeholder="A line or two is plenty" className="min-h-[90px]" data-testid="request-access-reason-textarea" />
                  </div>
                  <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-[11px] text-muted-foreground">Invite-only. We never share your email.</p>
                    <Button onClick={submit} disabled={sending} data-testid="request-access-submit-button">
                      {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Request access
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------ footer */
const Footer = () => (
  <footer className="border-t border-[hsl(var(--hairline))]">
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div>
        <Wordmark />
        <p className="text-xs text-muted-foreground mt-2">The smartest email client for founders. Your email data stays yours.</p>
      </div>
      <div className="flex items-center gap-6 text-xs text-muted-foreground">
        <button onClick={handleSignIn} className="hover:text-foreground transition-colors" data-testid="footer-signin-link">Sign in</button>
        <button onClick={() => scrollTo("access")} className="hover:text-foreground transition-colors" data-testid="footer-request-link">Request access</button>
        <span>© {new Date().getFullYear()} Discern</span>
      </div>
    </div>
  </footer>
);

/* ------------------------------------------------ page */
export default function Landing() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="force-light min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="force-light min-h-screen bg-background paper-grain" data-testid="landing-page">
      <Masthead user={user} />
      <SignalTicker />
      <Hero />
      <HowItWorks />
      <ProductScreens />
      <Principles />
      <Testimonials />
      <MorningDemo />
      <main className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        {FEATURES.map((f) => <FeatureSpread key={f.id} f={f} />)}
      </main>
      <FAQ />
      <RequestAccessSection />
      <Footer />
    </div>
  );
}
